import React, { useState, FormEvent, useRef, useCallback } from 'react';
import { Clock, Users, Image, Key, Upload, Plus, Film, Mic, Cloud, MoreVertical, Play, ChevronDown, Lock, BookAIcon, X } from 'lucide-react';
import Sidebar from '../../Sidebar/Sidebar';
import Navbar from '../../Navbars/UserNavbar';
import { DashboardApi } from '../../../apis/DashboardApi';
import { AxiosResponse } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { NavLink, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { shared } from "../../../reducer/SharedSlice";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";

interface FormData {
    name: string;
    description: string;
    recipient_ids: string[];
    type: string;
    files: File[];
    legacy: boolean;
}

interface Recipient {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    temporaryUrl?: string;
    group?: {
        name: string;
    };
    contact?: {
        name: string;
    };
}

const Dashboard = () => {
    const [selectedType, setSelectedType] = useState('video');
    const [uploading, setUploading] = useState(false);
    const [recipient, setRecipient] = useState<any>([]);
    const [previews, setPreviews] = useState<string[]>([]);
    const [demoModalOpen, setDemoModalOpen] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const userLoginData = useSelector((state: any) => state.data.login.value);

    const dispatch = useDispatch();
    const [formData, setFormData] = useState<FormData>({
        name: '',
        description: '',
        recipient_ids: [],
        type: 'video',
        files: [],
        legacy: false
    });

    // Demo video URL for Upload Media
    const demoVideoUrl = "https://youtu.be/sYxe3N3nXfY?si=dt81sFXD2M7T9dwH";

    const fileTypes = [
        { id: 'video', icon: Film, label: 'Videos', color: 'border-blue-500 text-blue-600' },
        { id: 'image', icon: Image, label: 'Photos', color: 'border-purple-500 text-purple-600' },
        { id: 'audio', icon: Mic, label: 'Audio', color: 'border-red-500 text-red-600' },
        { id: 'document', icon: BookAIcon, label: 'Document', color: 'border-green-500 text-green-600' }
    ];

    const navigate = useNavigate();

    React.useEffect(() => {
        DashboardApi.getMediaUsage().then(
            (response: AxiosResponse<any>) => {
                if (response?.data) {
                    if (userLoginData?.data?.user_data?.max_upload_limit_mb < response?.data?.data?.total_used_mb) {
                        window.location.href = '/upgrade';
                    }
                }
            }
        )

        DashboardApi.getRecipient().then(
            (response: AxiosResponse<any>) => {
                if (response?.data) {
                    setRecipient(response.data?.data)
                }
            }
        ).catch(function (error) {
            // handle error
        })
    }, []);

    // File validation function
    const validateFile = (file: File): boolean => {
        const maxSize = 100 * 1024 * 1024; // 100MB
        const allowedTypes: { [key: string]: string[] } = {
            image: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
            video: ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv'],
            audio: ['audio/mp3', 'audio/wav', 'audio/aac', 'audio/ogg'],
            document: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain']
        };

        // if (file.size > maxSize) {
        //     toast.error(`File ${file.name} is too large. Maximum size is 100MB.`);
        //     return false;
        // }

        // if (!allowedTypes[selectedType]?.includes(file.type)) {
        //     toast.error(`File ${file.name} is not a valid ${selectedType} file.`);
        //     return false;
        // }

        return true;
    };

    // Handle file processing
    const processFiles = useCallback((files: FileList | File[]) => {
        const fileArray = Array.from(files);
        let validFiles: File[] = [];

        if (selectedType === 'image') {
            // For images, allow multiple files
            validFiles = fileArray.filter(validateFile);
        } else {
            // For non-images, only take the first valid file
            const firstValidFile = fileArray.find(validateFile);
            if (firstValidFile) {
                validFiles = [firstValidFile];
                // Clear existing files for non-image types
                setFormData(prev => ({ ...prev, files: [] }));
                setPreviews([]);
            }
        }

        if (validFiles.length > 0) {
            // Add new files to existing files array
            setFormData(prev => ({
                ...prev,
                files: selectedType === 'image' ? [...prev.files, ...validFiles] : validFiles
            }));

            // Create and add new previews for images
            if (selectedType === 'image') {
                const newPreviews = validFiles.map(file => URL.createObjectURL(file));
                setPreviews(prev => [...prev, ...newPreviews]);
            }

            toast.success(`${validFiles.length} file(s) added successfully!`);
        }
    }, [selectedType]);

    // Drag and drop handlers
    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processFiles(files);
        }
    }, [processFiles]);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (formData.files.length === 0) {
            toast.error('Please select file(s)');
            return;
        }

        if (formData.recipient_ids.length === 0) {
            toast.error('Please select at least one recipient');
            return;
        }

        setUploading(true);

        try {
            const formDataToSend = new FormData();

            // Append all files
            if (selectedType === 'image') {
                formData.files.forEach((file, index) => {
                    formDataToSend.append('files[]', file);
                });
            } else {
                formDataToSend.append('files', formData.files[0]);
            }

            // Append other form data
            formDataToSend.append('name', formData.name);
            formDataToSend.append('description', formData.description);
            formData.recipient_ids.forEach((id) => {
                formDataToSend.append('recipients[]', id);
            });
            formDataToSend.append('type', selectedType);
            formDataToSend.append('legacy', formData.legacy ? 'on' : 'off');

            const response: any = await DashboardApi.UploadMedia(formDataToSend);

            if (response?.data?.status === false) {
                toast.error(response.data.message);
                return;
            }
            setPreviews([]);
            setFormData({
                name: '',
                description: '',
                recipient_ids: [],
                type: 'video',
                files: [],
                legacy: false
            })

            DashboardApi.getMediaUsage().then(
                (response: AxiosResponse<any>) => {
                    if (response?.data) {
                        dispatch(shared({ data: response?.data?.data }))
                        toast.success('Uploaded Successfully');
                        navigate('/my-media');
                        window.location.reload();
                    }
                }
            )

        } catch (error: any) {
            console.error('Upload error:', error.response?.data || error);
            toast.error(error.response?.data?.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, isMultiple: boolean = false) => {
        const files = e.target.files;
        if (!files) return;

        processFiles(files);

        // Reset the input value to allow selecting the same file again
        e.target.value = '';
    };

    const removeFile = (index: number) => {
        setFormData(prev => ({
            ...prev,
            files: prev.files.filter((_, i) => i !== index)
        }));

        if (selectedType === 'image') {
            URL.revokeObjectURL(previews[index]);
            setPreviews(prev => prev.filter((_, i) => i !== index));
        }
    };

    const handleTypeChange = (typeId: string) => {
        setSelectedType(typeId);
        setFormData(prev => ({ ...prev, type: typeId, files: [] }));
        setPreviews([]);
    };

    return (
        <div className="min-h-screen bg-white">
            <div className="flex flex-col md:flex-row">
                <Sidebar />
                <main className="flex-1 p-6 relative md:ml-64">
                    <Navbar />

                    {/* Header with Demo Button */}
                    <div className="flex justify-between items-center mb-8">
                        <h1 className="text-2xl font-semibold">Upload Media</h1>

                        <div className="flex items-center gap-4">
                            {/* Watch Demo Button with Animations */}
                            <button
                                onClick={() => setDemoModalOpen(true)}
                                className="group relative px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-medium overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-orange-300 animate-pulse"
                                style={{
                                    animation: 'pulse 2s infinite, glow 3s ease-in-out infinite alternate'
                                }}
                            >
                                {/* Animated background */}
                                <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                                {/* Shimmer effect */}
                                <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 group-hover:animate-shimmer"></div>

                                {/* Button content */}
                                <div className="relative flex items-center gap-2">
                                    <Play className="w-4 h-4 group-hover:animate-bounce" />
                                    <span className="font-semibold text-sm">Watch Demo</span>
                                </div>

                                {/* Floating dots animation */}
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
                        {/* File Type Selection */}
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                            {fileTypes.map((type) => {
                                const IconComponent = type.icon;
                                return (
                                    <button
                                        key={type.id}
                                        type="button"
                                        onClick={() => handleTypeChange(type.id)}
                                        className={`p-6 rounded-xl border-2 transition-all ${selectedType === type.id ? type.color : 'border-gray-200'
                                            }`}
                                    >
                                        <IconComponent className={`w-6 h-6 ${selectedType === type.id ? type.color : 'text-gray-500'}`} />
                                        <span className="text-sm font-medium">{type.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Form Fields */}
                        <input
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder={`${selectedType} Name`}
                            className="w-full p-3 rounded-lg border"
                            required
                        />

                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder="Description"
                            className="w-full p-3 rounded-lg border"
                        />

                        <div className="space-y-2">
                            <select
                                name="recipient_select"
                                onChange={(e) => {
                                    const selectedId = e.target.value;
                                    if (selectedId && !formData.recipient_ids.includes(selectedId)) {
                                        setFormData(prev => ({
                                            ...prev,
                                            recipient_ids: [...prev.recipient_ids, selectedId]
                                        }));
                                    }
                                    e.target.value = "";
                                }}
                                className="w-full p-3 rounded-lg border"
                                title='Please go to the recipient page to create one.'
                            >
                                <option value="">Select Recipients</option>
                                {recipient.map((recipient: Recipient) => (
                                    <option
                                        key={recipient.id}
                                        value={recipient.id}
                                        disabled={formData.recipient_ids.includes(String(recipient.id))}
                                    >
                                        {recipient.first_name} {recipient.last_name} ({recipient?.group?.name})
                                    </option>
                                ))}
                            </select>

                            {/* Selected Recipients Tags */}
                            <div className="flex gap-2 flex-wrap h-20 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors" >
                                {formData.recipient_ids.length === 0 ? (
                                    <div className=''>
                                        <p className="text-sm text-gray-500 pl-4 pt-3 text-center">No recipients selected </p>
                                    </div>
                                ) : (
                                    formData.recipient_ids.map((id) => {
                                        const selectedRecipient = recipient.find((r: any) => String(r.id) === id);
                                        if (!selectedRecipient) return null;

                                        return (
                                            <div key={id}>
                                                <div className="flex items-center gap-2 px-3 m-1 py-1 bg-gray-400 rounded-full text-sm">
                                                    <span>
                                                        {selectedRecipient.first_name} {selectedRecipient.last_name}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                recipient_ids: prev.recipient_ids.filter(rId => rId !== id)
                                                            }));
                                                        }}
                                                        className="text-gray-500 hover:text-gray-700"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* Enhanced File Upload with Drag & Drop */}
                        {selectedType === 'image' ? (
                            <div className="space-y-4">
                                {/* Image upload area */}
                                <div className="grid grid-cols-3 sm:grid-cols-5 gap-4">
                                    {/* Existing images */}
                                    {previews.map((preview, index) => (
                                        <div key={index} className="relative group">
                                            <img
                                                src={preview}
                                                alt={`Preview ${index + 1}`}
                                                className="w-full h-32 object-cover rounded-lg"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeFile(index)}
                                                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}

                                    {/* Add image button */}
                                    <label
                                        htmlFor="individual-file"
                                        className="flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                                    >
                                        <Plus className="w-8 h-8 text-gray-400" />
                                        <span className="text-sm text-gray-500">Add Image</span>
                                    </label>
                                </div>

                                {/* Hidden file inputs */}
                                <input
                                    type="file"
                                    id="individual-file"
                                    onChange={(e) => handleFileChange(e, false)}
                                    className="hidden"
                                    accept="image/*"
                                />

                                {/* Enhanced Drag & Drop upload area */}
                                <div
                                    className={`border-2 border-dashed rounded-lg p-8 transition-all duration-300 ${isDragOver
                                        ? 'border-blue-500 bg-blue-50 scale-105'
                                        : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                                        }`}
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                >
                                    <input
                                        type="file"
                                        onChange={(e) => handleFileChange(e, true)}
                                        className="hidden"
                                        id="bulk-files"
                                        multiple
                                        accept="image/*"
                                        ref={fileInputRef}
                                    />
                                    <label htmlFor="bulk-files" className="cursor-pointer block text-center">
                                        <div className={`transition-all duration-300 ${isDragOver ? 'scale-110' : ''}`}>
                                            <Upload className={`mx-auto w-12 h-12 mb-4 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
                                            <p className={`text-lg font-medium mb-2 ${isDragOver ? 'text-blue-600' : 'text-gray-700'}`}>
                                                {isDragOver ? 'Drop images here!' : 'Drag & drop multiple images here'}
                                            </p>
                                            <p className="text-gray-500 text-sm">
                                                or click to browse files
                                            </p>
                                            <div className="mt-4 text-xs text-gray-400">
                                                Supports: JPG, PNG, GIF, WebP (Max 100MB each)
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        ) : (
                            // Enhanced single file upload for non-image types with drag & drop
                            <div
                                className={`border-2 border-dashed rounded-lg p-8 transition-all duration-300 ${isDragOver
                                    ? 'border-blue-500 bg-blue-50 scale-105'
                                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                                    }`}
                                onDragEnter={handleDragEnter}
                                onDragLeave={handleDragLeave}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                            >
                                <input
                                    type="file"
                                    onChange={(e) => handleFileChange(e, false)}
                                    className="hidden"
                                    id="files"
                                    ref={fileInputRef}
                                />
                                <label htmlFor="files" className="cursor-pointer block text-center">
                                    <div className={`transition-all duration-300 ${isDragOver ? 'scale-110' : ''}`}>
                                        <Upload className={`mx-auto w-12 h-12 mb-4 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
                                        <p className={`text-lg font-medium mb-2 ${isDragOver ? 'text-blue-600' : 'text-gray-700'}`}>
                                            {isDragOver
                                                ? `Drop your ${selectedType} here!`
                                                : formData.files.length > 0
                                                    ? formData.files[0].name
                                                    : `Drag & drop ${selectedType} here`
                                            }
                                        </p>
                                        <p className="text-gray-500 text-sm">
                                            or click to browse files
                                        </p>
                                        <div className="mt-4 text-xs text-gray-400">
                                            Max file size: 100MB
                                        </div>
                                    </div>
                                </label>
                            </div>
                        )}

                        {/* Show selected files count */}
                        {formData.files.length > 0 && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <p className="text-green-700 text-sm font-medium">
                                    ✓ {formData.files.length} file(s) selected
                                </p>
                                <ul className="mt-2 text-xs text-green-600">
                                    {formData.files.map((file, index) => (
                                        <li key={index} className="flex justify-between items-center">
                                            <span>{file.name}</span>
                                            <span>({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Privacy Toggle */}
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                checked={formData.legacy}
                                onChange={(e) => setFormData({ ...formData, legacy: e.target.checked })}
                                className="mr-2"
                            />
                            <span>Make Legacy</span>
                        </div>

                        <div className="flex justify-end gap-4">
                            <button
                                type="button"
                                className="px-6 py-2 text-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={uploading}
                                className="px-6 py-2 bg-orange-500 text-white rounded-lg disabled:opacity-50"
                            >
                                {uploading ? 'Uploading...' : 'Save'}
                            </button>
                        </div>
                    </form>
                </main>
            </div>

            {/* Demo Video Modal */}
            <Modal
                open={demoModalOpen}
                onClose={() => setDemoModalOpen(false)}
                center
                classNames={{
                    overlay: 'bg-black bg-opacity-75',
                    modal: 'bg-transparent shadow-none max-w-4xl w-full p-0 m-4'
                }}
            >
                <div className="relative bg-black rounded-lg overflow-hidden">
                    {/* Close button */}
                    <button
                        onClick={() => setDemoModalOpen(false)}
                        className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>

                    {/* Video container */}
                    <div className="relative w-full h-0" style={{ paddingBottom: '56.25%' }}>
                        <video
                            src={'https://res.cloudinary.com/dobeex527/video/upload/v1752612329/upload_media_ic9bdx.mov'}
                            className="absolute top-0 left-0 w-full h-full object-contain"
                            controls
                            preload="metadata"
                            poster=""
                            title="Add Recipients Demo Video"
                        >
                            Your browser does not support the video tag.
                        </video>
                    </div>

                    {/* Video title */}
                    <div className="bg-black text-white p-4">
                        <h3 className="text-lg font-semibold">Upload Media Demo</h3>
                        <p className="text-gray-300 text-sm mt-1">
                            Learn how to upload and share your media content effectively
                        </p>
                    </div>
                </div>
            </Modal>

            <ToastContainer
                position="top-right"
                autoClose={2000}
                hideProgressBar={true}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />


        </div>
    );
};

export default Dashboard;